import React, { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTitle } from 'react-use';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { Modal } from '@savgroup-front-common/core/atoms/modal';
import SwipeableDrawer, {
  DRAWER_SIZE,
} from '@savgroup-front-common/core/atoms/SwipeableDrawer/SwipeableDrawer';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import {
  buildPageTitle,
  SUPPORTED_ICON_POSITIONS,
} from '@savgroup-front-common/core/helpers';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import ArrowNakedLeftIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedLeft.icon';
import { ArrowNakedRightIcon } from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedRight.icon';
import { MyAccountIcon } from '@savgroup-front-common/core/protons/IconsNewDesign/MyAccount.icon';
import { PickupPoint } from '@savgroup-front-common/types';
import { ICONS_TYPE } from '@savgroup-front-common/types/Icon';

import useGetPickupPoints from '@/view/app/hooks/useGetPickupPoints';
import { NewStepLayoutProfessional } from '@/view/app/NewLayout/NewStepLayout/NewStepLayoutProfessional';
import {
  PROFESSIONAL_STEP_CONFIG,
  PROFESSIONAL_STEP_PROGRESS,
} from '@/view/app/NewLayout/ProfessionalRoutes.config';
import { buildProfessionalRoute } from '@/view/app/NewLayout/ProfessionalRoutes.helpers';
import { AddressManagement } from '@/view/components/AddressManagement';
import HandlingCard from '@/view/components/HandlingCard';
import PickupPointSelector from '@/view/components/PickupPointSelector';
import { ResumeDeposit } from '@/view/components/ResumeDeposit';
import { ResumeSolution } from '@/view/components/ResumeSolution';

import { PageFooterContainer } from '../../../../app/NewLayout/NewStepLayout';
import { ResumeCard } from '../../components/ResumeCard';
import { IrshStepValues } from '../IrshPages.types';

import useClaimGroupDeliveryPage from './ClaimGroupDeliveryPage.hooks';
import {
  $DeliveryItemWrapper,
  $DeliveryResultWrapper,
  $DeliveryWrapper,
  $Heading,
  $HeadingIcon,
  $LeftColumn,
} from './ClaimGroupDeliveryPage.styles';
import messages from './messages';

const ClaimGroupDeliveryPage: React.FC<RoutedStepProps<IrshStepValues>> = ({
  onPreviousStep,
  onNextStep,
}) => {
  const { claimGroupId } = useParams<{ claimGroupId: string }>();
  const [openResumeModal, setOpenResumeModal] = useState(false);
  const {
    claimGroupConfirmation,
    groupedCarriers,
    formContext,
    changeSelection,
    onSubmit,
    isDeliveryHandlingLoading,
    chooseHandlingSelected,
    handlingSelected,
    homeSelectedAddressChanged,
    weight,
  } = useClaimGroupDeliveryPage({
    onNextStep,
  });

  const {
    setValue,
    formState: { isSubmitting, isValid },
  } = formContext;

  const {
    sellerId,
    carriers,
    addressSelected,
    pickupPoints,
    noPickupPoints,
    addresses,
    handleSetAddressSelected,
    selectedPickupPoint,
    pickupPointsIsLoading,
  } = useGetPickupPoints({
    selectedCarrierType: chooseHandlingSelected,
    groupedCarriers,
    claimGroupId,
    weight,
  });

  const customBuildUpdateSolutionUrl = claimGroupId
    ? buildProfessionalRoute({
        route: PROFESSIONAL_STEP_CONFIG.PROFESSIONAL_SOLUTION_PAGE,
        payload: { claimGroupId },
      })
    : undefined;

  const handleSetSelectedPickupPoint = (pickupPointSelected: PickupPoint) => {
    Promise.resolve(setValue('pickupPointSelected', pickupPointSelected)).then(
      () => onSubmit(),
    );
  };

  const handleOpenModal = () => setOpenResumeModal((prev) => !prev);

  useTitle(buildPageTitle([messages.claimGroupDeliveryTitlePage]));

  return (
    <>
      <NewStepLayoutProfessional
        stepName={PROFESSIONAL_STEP_CONFIG.PROFESSIONAL_DEPOSIT_PAGE}
        customSteps={PROFESSIONAL_STEP_PROGRESS}
      >
        <FormProvider {...formContext}>
          <form onSubmit={onSubmit}>
            <$DeliveryWrapper data-testid="DeliveryWrapper">
              <$LeftColumn>
                {claimGroupId && (
                  <ResumeSolution
                    claimGroupId={claimGroupId}
                    claimGroupConfirmation={claimGroupConfirmation}
                    customBuildUpdateSolutionUrl={customBuildUpdateSolutionUrl}
                  />
                )}
                <ResumeDeposit
                  claimGroupConfirmation={claimGroupConfirmation}
                />
                <$Heading>
                  <$HeadingIcon data-testid="ClaimGroupDeliveryHeadingIcon">
                    <MyAccountIcon icon={ICONS_TYPE.DELIVERY_ICON} />
                  </$HeadingIcon>
                  <SafeFormattedMessageWithoutSpread
                    message={messages.handlingTitle}
                  />
                </$Heading>
                <$DeliveryItemWrapper data-testid="DeliveryItemWrapper">
                  {isDeliveryHandlingLoading ? (
                    <BaseLoader />
                  ) : (
                    <HandlingCard groupedCarriers={groupedCarriers} />
                  )}
                </$DeliveryItemWrapper>
              </$LeftColumn>
              <$DeliveryResultWrapper>
                <ResumeCard />
              </$DeliveryResultWrapper>
            </$DeliveryWrapper>

            <SwipeableDrawer
              isOpen={handlingSelected}
              onClose={changeSelection}
              hasBackdrop
              hasPadding={false}
              hasBackdropScroll={false}
            >
              {handlingSelected && (
                <PickupPointSelector
                  maxDistance={200}
                  pickupPoints={pickupPoints}
                  selectedPickupPointId={
                    selectedPickupPoint ? selectedPickupPoint.id : undefined
                  }
                  onSelectPickupPoint={handleSetSelectedPickupPoint}
                  searchAddress={addressSelected}
                  userAddresses={addresses}
                  onAddressSelected={handleSetAddressSelected}
                  carriers={carriers}
                  pickupPointsIsLoading={pickupPointsIsLoading}
                  sellerId={sellerId}
                  noPickupPoints={noPickupPoints}
                />
              )}
            </SwipeableDrawer>
            <SwipeableDrawer
              isOpen={homeSelectedAddressChanged}
              size={DRAWER_SIZE.SMALL}
              onClose={changeSelection}
              hasBackdrop
              hasBackdropScroll={false}
            >
              <AddressManagement onSubmit={onSubmit} />
            </SwipeableDrawer>
          </form>
        </FormProvider>
        <Modal
          isOpen={openResumeModal}
          onClose={handleOpenModal}
          shouldShowPadding={false}
        >
          <ResumeCard />
        </Modal>
      </NewStepLayoutProfessional>

      <PageFooterContainer>
        <Button
          primary
          type={BUTTON_TYPES.BUTTON}
          icon={<ArrowNakedRightIcon />}
          position={SUPPORTED_ICON_POSITIONS.RIGHT}
          disabled={!isValid}
          isLoading={isSubmitting}
          onClick={onSubmit}
          dataTestId="nextButton"
        >
          <SafeFormattedMessageWithoutSpread message={messages.nextButton} />
        </Button>

        <Button
          type={BUTTON_TYPES.BUTTON}
          tertiary
          onClick={() => {
            onPreviousStep({});
          }}
          icon={<ArrowNakedLeftIcon />}
          position={SUPPORTED_ICON_POSITIONS.LEFT}
          dataTestId="previousButton"
        >
          <SafeFormattedMessageWithoutSpread
            message={messages.previousButton}
          />
        </Button>
      </PageFooterContainer>
    </>
  );
};

export default ClaimGroupDeliveryPage;
